// High-score persistence. localStorage-only — no backend, no accounts. Top-10
// list sorted by score, tie-broken by marshmallows toasted then most recent.
// Takes an injected Storage so it's unit-testable headless.

export interface ScoreEntry {
  /** Final run score. */
  score: number;
  /** Marshmallows toasted this run. */
  marshmallowsDone: number;
  /** ISO timestamp of when the run ended. */
  date: string;
}

export interface SubmitResult {
  /** 1-based rank if the score made the top-10, else null. */
  rank: number | null;
  /** True iff this score is now the single best on the board. */
  isNewRecord: boolean;
  /** The updated, persisted top-10 list. */
  scores: ScoreEntry[];
}

export const STORAGE_KEY = 'marshmallow-madness:high-scores';
export const MAX_SCORES = 10;

function defaultStorage(): Storage {
  return (globalThis as unknown as { localStorage: Storage }).localStorage;
}

/** Read scores from storage. Returns [] on missing / corrupt data. */
export function loadScores(storage: Storage = defaultStorage()): ScoreEntry[] {
  const raw = storage.getItem(STORAGE_KEY);
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isScoreEntry);
  } catch {
    return [];
  }
}

/** Persist scores back to storage. */
export function saveScores(scores: ScoreEntry[], storage: Storage = defaultStorage()): void {
  storage.setItem(STORAGE_KEY, JSON.stringify(scores));
}

/** Sort: higher score first; ties broken by marshmallows toasted, then recency. */
export function sortScores(scores: readonly ScoreEntry[]): ScoreEntry[] {
  return [...scores].sort(compareScores);
}

/** Top N scores (default MAX_SCORES). */
export function topScores(n: number = MAX_SCORES, storage: Storage = defaultStorage()): ScoreEntry[] {
  return sortScores(loadScores(storage)).slice(0, Math.max(0, n));
}

/**
 * Insert a score, sort, clamp to MAX_SCORES, persist. Returns the entry's rank
 * (1-based, or null if it didn't make the board), whether it's the new #1, and
 * the updated list.
 */
export function submitScore(entry: ScoreEntry, storage: Storage = defaultStorage()): SubmitResult {
  const previous = sortScores(loadScores(storage));

  // Tag the new entry with a unique marker so we can find its rank by identity
  // rather than reference/value equality — on exact ties (same score, same
  // marshmallows, same date) value-based indexOf could match the wrong row or
  // miss the entry entirely once it's sliced past MAX_SCORES.
  const tag = Symbol('submitted');
  type Tagged = ScoreEntry & { [tag]?: true };
  const marked: Tagged = { ...entry, [tag]: true };

  const ranked: Tagged[] = [...previous, marked].sort(compareScores);
  const index = ranked.findIndex((e: Tagged) => e[tag] === true);
  const rank = index >= 0 && index < MAX_SCORES ? index + 1 : null;

  // Strip the marker before persisting / returning so storage stays clean.
  const scores: ScoreEntry[] = ranked
    .slice(0, MAX_SCORES)
    .map((e) => ({ score: e.score, marshmallowsDone: e.marshmallowsDone, date: e.date }));
  saveScores(scores, storage);

  const prevBest = previous[0];
  const isNewRecord =
    rank === 1 && (prevBest === undefined || compareScores(entry, prevBest) < 0);

  return { rank, isNewRecord, scores };
}

function compareScores(a: ScoreEntry, b: ScoreEntry): number {
  if (a.score !== b.score) return b.score - a.score;
  if (a.marshmallowsDone !== b.marshmallowsDone) return b.marshmallowsDone - a.marshmallowsDone;
  // More recent first on a full tie.
  return b.date.localeCompare(a.date);
}

function isScoreEntry(x: unknown): x is ScoreEntry {
  if (typeof x !== 'object' || x === null) return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o.score === 'number' &&
    typeof o.marshmallowsDone === 'number' &&
    typeof o.date === 'string'
  );
}
